import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppShell, PageContainer } from '../../features/ui-system';
import axios from 'axios';
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  Clock3,
  Download,
  ExternalLink,
  Loader2,
  MessageCircle,
  RefreshCw,
  SendHorizontal,
  Settings,
  Tag,
  Ticket,
  UserRound,
  Wrench,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import StaffPageHeader from '../../components/StaffPageHeader';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import { resolveBackendBaseUrl } from '../../utils/runtimeConfig';

const API = `${resolveBackendBaseUrl()}/api`;
const AUDIT_EXPORT_PRESET_KEY = 'wa_ai_audit_export_preset_v1';
const AUDIT_EXPORT_LIMITS_KEY = 'wa_ai_audit_export_limits_v1';

const QUICK_REPLIES = [
  'Halo, kami bantu cek kendala Anda sekarang ya.',
  'Mohon restart modem dulu 1-2 menit, lalu kabari hasilnya.',
  'Baik, laporan Anda kami teruskan ke tim teknis.',
  'Terima kasih, kami lanjutkan prosesnya.',
];

const ISSUE_TAGS = ['network_issue', 'billing_issue', 'wifi_issue'];

const normalizePhone = (value) => String(value || '').replace(/[^0-9]/g, '').replace(/^0/, '62');

const toDateSafe = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const formatDateSafe = (value) => {
  const date = toDateSafe(value);
  if (!date) return '-';
  return date.toLocaleString('id-ID');
};

const formatRelative = (value) => {
  const date = toDateSafe(value);
  if (!date) return '-';
  const diffMin = Math.max(0, Math.floor((Date.now() - date.getTime()) / 60000));
  if (diffMin < 1) return 'barusan';
  if (diffMin < 60) return `${diffMin}m lalu`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}j lalu`;
  return `${Math.floor(diffHour / 24)}h lalu`;
};

const THREAD_SLA_MINUTES = 15;

const deriveThreadSlaFromThreadRow = (thread) => {
  const lastDirection = String(thread?.last_direction || '').toLowerCase();
  const lastAt = toDateSafe(thread?.last_message_at || thread?.updated_at);
  if (!lastAt || lastDirection !== 'inbound') {
    return { pending: false, remainingMinutes: null, risk: 'low', label: 'SLA aman' };
  }
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - lastAt.getTime()) / 60000));
  const remainingMinutes = Math.max(0, THREAD_SLA_MINUTES - elapsedMinutes);
  if (remainingMinutes <= 0) {
    return { pending: true, remainingMinutes: 0, risk: 'high', label: 'SLA terlewat' };
  }
  if (remainingMinutes <= 5) {
    return { pending: true, remainingMinutes, risk: 'medium', label: 'SLA dekat' };
  }
  return { pending: true, remainingMinutes, risk: 'low', label: 'SLA berjalan' };
};

const normalizeIssueTagLabel = (tag) => {
  if (tag === 'network_issue') return 'Gangguan Jaringan';
  if (tag === 'billing_issue') return 'Masalah Tagihan';
  if (tag === 'wifi_issue') return 'Masalah WiFi';
  return 'Umum';
};

const tagChipClass = (tag) => {
  if (tag === 'network_issue') return 'border-red-400/40 bg-red-500/15 text-red-200';
  if (tag === 'billing_issue') return 'border-amber-400/40 bg-amber-500/15 text-amber-200';
  if (tag === 'wifi_issue') return 'border-cyan-400/40 bg-cyan-500/15 text-cyan-200';
  return 'border-slate-400/40 bg-slate-500/15 text-slate-200';
};

const detectIssueFromText = (textRaw) => {
  const text = String(textRaw || '').toLowerCase();
  if (!text.trim()) {
    return {
      tag: 'network_issue',
      confidence: 42,
      summary: 'Belum ada cukup konteks pesan untuk klasifikasi otomatis.',
      cause: 'Konten pesan pelanggan belum cukup untuk deteksi akar masalah.',
      suggestions: ['Boleh info kendala utamanya, internet putus / lambat / tagihan?'],
    };
  }

  const billingKeywords = ['tagihan', 'bayar', 'pembayaran', 'invoice', 'tempo', 'billing'];
  const wifiKeywords = ['wifi', 'wi-fi', 'ssid', 'password', 'sandi', 'router'];
  const networkKeywords = ['putus', 'disconnect', 'offline', 'loss', 'lemot', 'lambat', 'gangguan', 'internet'];

  const score = {
    billing_issue: billingKeywords.reduce((acc, key) => acc + (text.includes(key) ? 1 : 0), 0),
    wifi_issue: wifiKeywords.reduce((acc, key) => acc + (text.includes(key) ? 1 : 0), 0),
    network_issue: networkKeywords.reduce((acc, key) => acc + (text.includes(key) ? 1 : 0), 0),
  };

  const top = Object.entries(score).sort((a, b) => b[1] - a[1])[0] || ['network_issue', 0];
  const tag = top[0];
  const rawScore = top[1];
  const confidence = Math.max(45, Math.min(95, 45 + (rawScore * 16)));

  if (tag === 'billing_issue') {
    return {
      tag,
      confidence,
      summary: 'Pelanggan kemungkinan menanyakan tagihan/pembayaran.',
      cause: 'Kata kunci billing dominan di percakapan.',
      suggestions: [
        'Kami cek status tagihan Anda dulu ya, mohon tunggu sebentar.',
        'Jika sudah transfer, mohon kirim bukti agar kami bantu validasi lebih cepat.',
      ],
    };
  }

  if (tag === 'wifi_issue') {
    return {
      tag,
      confidence,
      summary: 'Keluhan mengarah ke pengaturan atau performa WiFi lokal.',
      cause: 'Percakapan berisi indikator masalah SSID/password/sinyal WiFi.',
      suggestions: [
        'Silakan restart modem/router 1-2 menit dulu, lalu coba sambungkan ulang.',
        'Kami bisa bantu pandu ubah pengaturan WiFi jika diperlukan.',
      ],
    };
  }

  return {
    tag: 'network_issue',
    confidence,
    summary: 'Keluhan dominan mengarah ke gangguan konektivitas jaringan.',
    cause: 'Kata kunci putus/lambat/gangguan terdeteksi dominan.',
    suggestions: [
      'Kami sedang cek status jaringan di area Anda, mohon tunggu sebentar.',
      'Jika lampu LOS merah atau internet tetap putus, kami siapkan tiket teknisi.',
    ],
  };
};

const AdminWhatsAppLive = () => {
  const navigate = useNavigate();

  const [summary, setSummary] = useState({
    total_threads: 0,
    open_threads: 0,
    closed_threads: 0,
    unread_threads: 0,
    inbound_today: 0,
  });
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState('');
  const [messages, setMessages] = useState([]);
  const [composeText, setComposeText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [threadFilter, setThreadFilter] = useState('open');
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [contextLoading, setContextLoading] = useState(false);
  const [customerContext, setCustomerContext] = useState(null);
  const [selectedTagByThread, setSelectedTagByThread] = useState({});
  const [savingTagThreadId, setSavingTagThreadId] = useState('');
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditExporting, setAuditExporting] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditResult, setAuditResult] = useState(null);
  const [auditExportLimits, setAuditExportLimits] = useState(() => {
    try {
      const raw = localStorage.getItem(AUDIT_EXPORT_LIMITS_KEY);
      if (!raw) return { actionLimit: 200, messageLimit: 150 };
      const parsed = JSON.parse(raw);
      return {
        actionLimit: Number(parsed?.actionLimit) || 200,
        messageLimit: Number(parsed?.messageLimit) || 150,
      };
    } catch {
      return { actionLimit: 200, messageLimit: 150 };
    }
  });
  const [auditExportPresetKey, setAuditExportPresetKey] = useState(() => localStorage.getItem(AUDIT_EXPORT_PRESET_KEY) || 'custom');

  const auditExportPresets = useMemo(() => ([
    { key: 'small', label: 'Kecil', actionLimit: 50, messageLimit: 20 },
    { key: 'medium', label: 'Sedang', actionLimit: 200, messageLimit: 100 },
    { key: 'full', label: 'Lengkap', actionLimit: 500, messageLimit: 500 },
  ]), []);

  useEffect(() => {
    localStorage.setItem(AUDIT_EXPORT_LIMITS_KEY, JSON.stringify(auditExportLimits));
  }, [auditExportLimits]);

  useEffect(() => {
    localStorage.setItem(AUDIT_EXPORT_PRESET_KEY, auditExportPresetKey);
  }, [auditExportPresetKey]);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const selectedThread = useMemo(
    () => threads.find((item) => item.id === selectedThreadId) || null,
    [threads, selectedThreadId],
  );

  const selectedThreadMessages = useMemo(
    () => (Array.isArray(messages) ? messages : []),
    [messages],
  );

  const latestInboundMessage = useMemo(
    () => [...selectedThreadMessages]
      .reverse()
      .find((item) => String(item?.direction || '').toLowerCase() === 'inbound') || null,
    [selectedThreadMessages],
  );

  const aiInsight = useMemo(() => {
    const sourceText = `${latestInboundMessage?.text || ''} ${composeText || ''}`.trim();
    return detectIssueFromText(sourceText);
  }, [composeText, latestInboundMessage]);

  const activeTag = useMemo(() => {
    const manualTag = selectedTagByThread[selectedThreadId];
    const persistedTag = String(selectedThread?.issue_tag || '').trim().toLowerCase();
    return manualTag || persistedTag || aiInsight.tag;
  }, [aiInsight.tag, selectedTagByThread, selectedThread, selectedThreadId]);

  const slaIndicator = useMemo(() => {
    const inbound = [...selectedThreadMessages]
      .reverse()
      .find((item) => String(item?.direction || '').toLowerCase() === 'inbound');
    const outbound = [...selectedThreadMessages]
      .reverse()
      .find((item) => String(item?.direction || '').toLowerCase() === 'outbound');

    const inboundAt = toDateSafe(inbound?.created_at);
    const outboundAt = toDateSafe(outbound?.created_at);

    if (!inboundAt) {
      return { responseMinutes: null, risk: 'low', label: 'Belum ada pesan masuk' };
    }

    const answered = outboundAt && outboundAt > inboundAt;
    if (answered) {
      const responseMinutes = Math.max(0, Math.floor((outboundAt.getTime() - inboundAt.getTime()) / 60000));
      return {
        responseMinutes,
        risk: responseMinutes > THREAD_SLA_MINUTES ? 'high' : 'low',
        label: responseMinutes > THREAD_SLA_MINUTES ? 'Respons melebihi SLA' : 'Sudah direspons',
        pending: false,
        remainingMinutes: null,
      };
    }

    const responseMinutes = Math.max(0, Math.floor((Date.now() - inboundAt.getTime()) / 60000));
    const remainingMinutes = Math.max(0, THREAD_SLA_MINUTES - responseMinutes);
    if (remainingMinutes <= 0) {
      return { responseMinutes, risk: 'high', label: 'SLA terlewat', pending: true, remainingMinutes: 0 };
    }
    if (remainingMinutes <= 5) {
      return { responseMinutes, risk: 'medium', label: 'SLA mendekati batas', pending: true, remainingMinutes };
    }
    return { responseMinutes, risk: 'low', label: 'SLA aman', pending: true, remainingMinutes };
  }, [selectedThreadMessages]);

  const fetchSummary = useCallback(async () => {
    const res = await axios.get(`${API}/whatsapp/live/summary`, { headers: getHeaders() });
    setSummary((prev) => ({ ...prev, ...(res.data || {}) }));
  }, [getHeaders]);

  const fetchThreads = useCallback(async (searchKeyword = '', state = threadFilter) => {
    const res = await axios.get(`${API}/whatsapp/live/threads`, {
      headers: getHeaders(),
      params: { limit: 100, q: searchKeyword || undefined, state: state || undefined },
    });
    const items = Array.isArray(res.data?.items) ? res.data.items : [];
    const sortedItems = [...items].sort((a, b) => {
      const slaA = deriveThreadSlaFromThreadRow(a);
      const slaB = deriveThreadSlaFromThreadRow(b);
      const riskWeight = { high: 3, medium: 2, low: 1 };
      const riskDiff = (riskWeight[slaB.risk] || 0) - (riskWeight[slaA.risk] || 0);
      if (riskDiff !== 0) return riskDiff;

      const unreadDiff = Number(b?.unread_count_admin || 0) - Number(a?.unread_count_admin || 0);
      if (unreadDiff !== 0) return unreadDiff;

      const aTs = new Date(a?.last_message_at || a?.updated_at || 0).getTime();
      const bTs = new Date(b?.last_message_at || b?.updated_at || 0).getTime();
      return bTs - aTs;
    });
    setThreads(sortedItems);
    if (!selectedThreadId && sortedItems[0]?.id) setSelectedThreadId(sortedItems[0].id);
    if (selectedThreadId && !sortedItems.some((it) => it.id === selectedThreadId)) {
      setSelectedThreadId(sortedItems[0]?.id || '');
    }
  }, [getHeaders, selectedThreadId, threadFilter]);

  const fetchMessages = useCallback(async (threadId, options = {}) => {
    if (!threadId) return;
    const markRead = options.markRead !== false;
    const res = await axios.get(`${API}/whatsapp/live/threads/${threadId}/messages`, {
      headers: getHeaders(),
      params: { limit: 200 },
    });
    setMessages(Array.isArray(res.data?.items) ? res.data.items : []);
    if (markRead) {
      await axios.post(`${API}/whatsapp/live/threads/${threadId}/mark-read`, {}, { headers: getHeaders() }).catch(() => {});
    }
  }, [getHeaders]);

  const fetchCustomerContext = useCallback(async (thread) => {
    if (!thread) {
      setCustomerContext(null);
      return;
    }

    const waPhone = normalizePhone(thread?.wa_id || thread?.contact_phone || '');
    if (!waPhone) {
      setCustomerContext(null);
      return;
    }

    setContextLoading(true);
    try {
      const lookupRes = await axios.get(`${API}/whatsapp/live/customer-context`, {
        headers: getHeaders(),
        params: { phone: waPhone },
      });
      if (!lookupRes?.data?.matched) {
        setCustomerContext({ customer: null, context: null });
        return;
      }
      const payload = lookupRes.data || {};
      const contextPayload = payload?.context || {};
      setCustomerContext({
        customer: payload?.customer || contextPayload?.customer || null,
        context: contextPayload || null,
      });
    } catch {
      setCustomerContext({ customer: null, context: null });
    } finally {
      setContextLoading(false);
    }
  }, [getHeaders]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([fetchSummary(), fetchThreads(search, threadFilter)]);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal memuat Pusat Dukungan WhatsApp');
    } finally {
      setLoading(false);
    }
  }, [fetchSummary, fetchThreads, search, threadFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!selectedThreadId) {
      setMessages([]);
      return;
    }
    fetchMessages(selectedThreadId, { markRead: true });
  }, [fetchMessages, selectedThreadId]);

  useEffect(() => {
    if (!selectedThread) return;
    fetchCustomerContext(selectedThread);
  }, [fetchCustomerContext, selectedThread]);

  useEffect(() => {
    setAuditResult(null);
    setAuditError('');
  }, [selectedThreadId]);

  useEffect(() => {
    if (!autoRefresh) return undefined;
    const timer = setInterval(async () => {
      try {
        await fetchSummary();
        await fetchThreads(search, threadFilter);
        if (selectedThreadId) {
          await fetchMessages(selectedThreadId, { markRead: false });
        }
      } catch {
        // muat ulang diam-diam
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [autoRefresh, fetchMessages, fetchSummary, fetchThreads, search, selectedThreadId, threadFilter]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchSummary();
      await fetchThreads(search, threadFilter);
      if (selectedThreadId) await fetchMessages(selectedThreadId, { markRead: false });
      if (selectedThread) await fetchCustomerContext(selectedThread);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal memuat ulang pusat dukungan');
    } finally {
      setRefreshing(false);
    }
  };

  const handleSend = async () => {
    if (!selectedThreadId || !composeText.trim()) return;
    setSending(true);
    try {
      await axios.post(
        `${API}/whatsapp/live/threads/${selectedThreadId}/send`,
        { text: composeText.trim() },
        { headers: getHeaders() },
      );
      setComposeText('');
      await fetchMessages(selectedThreadId, { markRead: true });
      await fetchThreads(search, threadFilter);
      await fetchSummary();
      toast.success('Balasan terkirim');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal mengirim balasan');
    } finally {
      setSending(false);
    }
  };

  const handleSelectIssueTag = async (tag) => {
    if (!selectedThreadId) return;
    const nextTag = String(tag || '').trim().toLowerCase();
    if (!ISSUE_TAGS.includes(nextTag)) return;

    setSelectedTagByThread((prev) => ({ ...prev, [selectedThreadId]: nextTag }));
    setThreads((prev) => prev.map((item) => (
      item.id === selectedThreadId ? { ...item, issue_tag: nextTag } : item
    )));
    setSavingTagThreadId(selectedThreadId);
    try {
      await axios.post(
        `${API}/whatsapp/live/threads/${selectedThreadId}/tag`,
        {
          issue_tag: nextTag,
          ai_summary: aiInsight.summary,
          ai_cause: aiInsight.cause,
          ai_confidence: aiInsight.confidence,
          ai_recommendations: aiInsight.suggestions,
        },
        { headers: getHeaders() },
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal menyimpan tag chat');
    } finally {
      setSavingTagThreadId('');
    }
  };

  const handleCreateTicket = async (options = {}) => {
    const customerId = String(customerContext?.customer?.id || '').trim();
    if (!customerId) {
      toast.error('Customer belum teridentifikasi dari nomor WhatsApp.');
      return;
    }
    const includeAiSummary = Boolean(options?.includeAiSummary);
    const issueNotes = includeAiSummary
      ? `[WhatsApp][AI] ${aiInsight.summary} | Penyebab: ${aiInsight.cause || '-'} | Kepercayaan: ${aiInsight.confidence}% | Tag: ${normalizeIssueTagLabel(activeTag)}`
      : `[WhatsApp] ${aiInsight.summary} | Tag: ${normalizeIssueTagLabel(activeTag)}`;
    setCreatingTicket(true);
    try {
      await axios.post(
        `${API}/field-tickets`,
        {
          customer_id: customerId,
          issue_notes: issueNotes,
        },
        { headers: getHeaders() },
      );
      toast.success('Tiket berhasil dibuat dari percakapan.');
      await fetchCustomerContext(selectedThread);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal membuat tiket dari chat');
    } finally {
      setCreatingTicket(false);
    }
  };

  const buildAuditContextOverride = useCallback(() => {
    const ctx = customerContext?.context || {};
    const customer = customerContext?.customer || null;
    return {
      customer,
      device: ctx?.device_status || ctx?.device || null,
      billing: ctx?.billing || null,
      tickets: Array.isArray(ctx?.tickets) ? ctx.tickets : [],
      incidents: Array.isArray(ctx?.incidents) ? ctx.incidents : [],
    };
  }, [customerContext]);

  const handleRunAiAudit = async () => {
    const text = String(composeText || latestInboundMessage?.text || '').trim();
    if (!text) {
      toast.error('Belum ada teks untuk disimulasikan.');
      return;
    }
    setAuditLoading(true);
    setAuditError('');
    try {
      const payload = {
        text,
        wa_id: selectedThread?.wa_id || selectedThread?.contact_phone || '',
        context_override: buildAuditContextOverride(),
        unresolved_attempts: Number(selectedThread?.unresolved_attempts || 0),
        vip_flag: Boolean(selectedThread?.vip_flag),
        high_risk_flag: Boolean(selectedThread?.high_risk_flag),
        include_trace: true,
      };
      const res = await axios.post(`${API}/whatsapp/ai/simulate-policy`, payload, { headers: getHeaders() });
      setAuditResult(res?.data || null);
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Gagal menjalankan simulasi AI policy.';
      setAuditError(msg);
      toast.error(msg);
    } finally {
      setAuditLoading(false);
    }
  };

  const handleExportAiAuditJson = async (options = {}) => {
    if (!selectedThread) {
      toast.error('Pilih percakapan terlebih dahulu.');
      return;
    }
    const includeMessages = options.includeMessages !== false;
    const actionLimit = Number(auditExportLimits?.actionLimit || 200);
    const messageLimit = Number(auditExportLimits?.messageLimit || 150);
    setAuditExporting(true);
    try {
      const params = {
        wa_id: selectedThread?.wa_id || selectedThread?.contact_phone || undefined,
        external_thread_id: selectedThread?.id || undefined,
        action_limit: Number.isFinite(actionLimit) ? Math.max(1, Math.min(500, actionLimit)) : 200,
        message_limit: includeMessages
          ? (Number.isFinite(messageLimit) ? Math.max(1, Math.min(500, messageLimit)) : 150)
          : 0,
        include_messages: includeMessages,
      };
      const res = await axios.get(`${API}/whatsapp/ai/audit-export`, {
        headers: getHeaders(),
        params,
      });
      const payload = res?.data?.data || {};
      const text = JSON.stringify(payload, null, 2);
      const blob = new Blob([text], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      const waSuffix = String(selectedThread?.wa_id || selectedThread?.contact_phone || 'thread').replace(/[^0-9a-zA-Z_-]/g, '');
      const fileName = `wa-ai-audit-${includeMessages ? 'full' : 'compact'}-${waSuffix || 'thread'}-${ts}.json`;
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success(`Audit JSON ${includeMessages ? 'lengkap' : 'ringkas'} berhasil diunduh.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Gagal mengunduh audit JSON.');
    } finally {
      setAuditExporting(false);
    }
  };

  const linkedCustomerId = String(customerContext?.customer?.id || '').trim();

  if (loading) {
    return (
      <StaffPageLoadingOverlay text="Memuat Pusat Dukungan WhatsApp..." />
    );
  }

  return (
    <>
      <AppShell>
      <PageContainer>
        <div className="w-full">
          <StaffPageHeader
            eyebrow="DUKUNGAN"
            title="Pusat Dukungan AI WhatsApp"
            subtitle="Percakapan real-time + konteks pelanggan + rekomendasi AI untuk respons lebih cepat."
            titleClassName="text-white"
            subtitleClassName="text-cyan-100/90"
            actions={(
              <div className="flex items-center gap-2">
                <label className="inline-flex items-center gap-2 rounded-lg border border-emerald-400/40 bg-emerald-500/15 px-3 py-2 text-xs text-emerald-200">
                  <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                  Pembaruan otomatis 15 detik
                </label>
                <Button type="button" variant="outline" className="btn-secondary" onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Muat Ulang
                </Button>
                <Button type="button" variant="outline" className="btn-secondary" onClick={() => navigate('/dadu/pengaturan?tab=whatsapp')}>
                  <Settings className="mr-2 h-4 w-4" />
                  Integrasi WhatsApp
                </Button>
              </div>
            )}
          />

          <section className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <div className="ktc-list-card rounded-xl border p-3"><p className="text-xs text-gray-400">Total Thread</p><p className="text-2xl font-bold text-white">{summary.total_threads}</p></div>
            <div className="ktc-list-card rounded-xl border p-3"><p className="text-xs text-gray-400">Terbuka</p><p className="text-2xl font-bold text-emerald-300">{summary.open_threads}</p></div>
            <div className="ktc-list-card rounded-xl border p-3"><p className="text-xs text-gray-400">Belum Dibaca</p><p className="text-2xl font-bold text-rose-300">{summary.unread_threads}</p></div>
            <div className="ktc-list-card rounded-xl border p-3"><p className="text-xs text-gray-400">Inbound Hari Ini</p><p className="text-2xl font-bold text-cyan-300">{summary.inbound_today}</p></div>
            <div className="ktc-list-card rounded-xl border p-3">
              <p className="text-xs text-gray-400">SLA Saat Ini</p>
              <p className={`text-xl font-bold ${slaIndicator.risk === 'high' ? 'text-rose-300' : slaIndicator.risk === 'medium' ? 'text-amber-300' : 'text-emerald-300'}`}>{slaIndicator.label}</p>
              <p className="text-[11px] text-slate-400">
                {slaIndicator.pending
                  ? (slaIndicator.remainingMinutes == null ? '-' : `${slaIndicator.remainingMinutes}m tersisa`)
                  : (slaIndicator.responseMinutes == null ? '-' : `Respon ${slaIndicator.responseMinutes}m`)}
              </p>
            </div>
          </section>

          <section className="mt-5 grid gap-4 lg:grid-cols-[320px_1fr_360px]">
            <div className="ktc-list-card rounded-2xl border p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <MessageCircle className="h-4 w-4 text-cyan-300" />
                <h3 className="font-semibold text-white">Daftar Percakapan</h3>
              </div>

              <div className="mb-3 grid grid-cols-3 gap-2">
                {[{ key: 'open', label: 'Terbuka' }, { key: 'unread', label: 'Belum Dibaca' }, { key: 'closed', label: 'Ditutup' }].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setThreadFilter(item.key)}
                    className={`rounded-md border px-2 py-1 text-xs ${threadFilter === item.key ? 'border-cyan-300 bg-cyan-500/20 text-cyan-100' : 'border-white/20 text-slate-300 bg-[#0f1b30]/70'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') fetchThreads(e.currentTarget.value, threadFilter);
                }}
                placeholder="Cari nama / nomor WA"
                className="mb-3 w-full rounded-lg border border-white/20 bg-[#0A0A0A] px-3 py-2 text-sm text-white"
              />

              <div className="max-h-[620px] space-y-2 overflow-y-auto pr-1">
                {threads.map((thread) => {
                  const threadTag = selectedTagByThread[thread.id] || String(thread.issue_tag || '').trim().toLowerCase() || 'network_issue';
                  const rowSla = deriveThreadSlaFromThreadRow(thread);
                  return (
                    <button
                      key={thread.id}
                      type="button"
                      onClick={() => setSelectedThreadId(thread.id)}
                      className={`w-full rounded-lg border px-3 py-3 text-left transition ${selectedThreadId === thread.id ? 'border-cyan-300 bg-cyan-500/20' : 'border-white/20 bg-[#0f1b30]/75 hover:bg-cyan-500/10'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate font-semibold text-white">{thread.customer_name || thread.contact_name || thread.wa_id}</p>
                        {Number(thread.unread_count_admin || 0) > 0 ? (
                          <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">{thread.unread_count_admin}</span>
                        ) : null}
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-400">{thread.wa_id}</p>
                      <p className="mt-1 truncate text-sm text-slate-300">{thread.last_message_preview || '-'}</p>
                      <div className="mt-2 flex items-center justify-between text-[11px]">
                        <span className="text-slate-400">{formatRelative(thread.updated_at || thread.last_message_at)}</span>
                        <span className={`rounded-full border px-2 py-0.5 ${tagChipClass(threadTag)}`}>{normalizeIssueTagLabel(threadTag)}</span>
                      </div>
                      <div className="mt-1 text-[11px]">
                        <span className={`${rowSla.risk === 'high' ? 'text-rose-300' : rowSla.risk === 'medium' ? 'text-amber-300' : 'text-slate-300'}`}>
                          {rowSla.pending
                            ? `${rowSla.label}${rowSla.remainingMinutes == null ? '' : ` ï¿½ ${rowSla.remainingMinutes}m`}`
                            : rowSla.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
                {threads.length === 0 ? <p className="text-sm text-slate-400">Belum ada percakapan.</p> : null}
              </div>
            </div>

            <div className="ktc-list-card rounded-2xl border p-4 shadow-sm">
              {selectedThread ? (
                <>
                  <div className="flex items-center justify-between gap-2 border-b border-white/20 pb-3">
                    <div>
                      <p className="font-semibold text-white">{selectedThread.customer_name || selectedThread.contact_name || selectedThread.wa_id}</p>
                      <p className="text-xs text-gray-400">{selectedThread.wa_id}</p>
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-[#0f1b30]/80 px-3 py-1.5 text-xs text-slate-200">
                      <Clock3 className="h-3.5 w-3.5" />
                      {slaIndicator.pending
                        ? `SLA ${slaIndicator.remainingMinutes == null ? '-' : `${slaIndicator.remainingMinutes}m`}`
                        : `Respon ${slaIndicator.responseMinutes == null ? '-' : `${slaIndicator.responseMinutes}m`}`}
                    </div>
                  </div>

                  <div className="mt-3 h-[480px] overflow-y-auto rounded-lg border border-white/20 bg-[#0f1b30]/70 p-3">
                    {selectedThreadMessages.map((item) => (
                      <div key={item.id} className={`mb-2 flex ${item.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[82%] rounded-xl px-3 py-2 text-sm ${item.direction === 'outbound' ? 'bg-cyan-600 text-white' : 'bg-[#0b1527] text-slate-200 border border-white/20'}`}>
                          <p>{item.text || '-'}</p>
                          <p className={`mt-1 text-[11px] ${item.direction === 'outbound' ? 'text-cyan-100' : 'text-slate-500'}`}>
                            {formatDateSafe(item.created_at)} ï¿½ {item.status || 'received'}
                          </p>
                        </div>
                      </div>
                    ))}
                    {selectedThreadMessages.length === 0 ? <p className="text-sm text-slate-400">Belum ada pesan.</p> : null}
                  </div>

                  <div className="mt-3 rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 text-cyan-100">
                        <Bot className="h-4 w-4" />
                        <p className="text-sm font-semibold">Asisten AI</p>
                      </div>
                      <span className="rounded-full border border-cyan-300/40 bg-cyan-400/10 px-2 py-0.5 text-xs text-cyan-100">Kepercayaan {aiInsight.confidence}%</span>
                    </div>
                    <p className="mt-2 text-xs text-cyan-100/90">{aiInsight.summary}</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ISSUE_TAGS.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className={`rounded-full border px-2 py-1 text-[11px] ${activeTag === tag ? tagChipClass(tag) : 'border-slate-500/40 bg-slate-500/10 text-slate-300'}`}
                          onClick={() => handleSelectIssueTag(tag)}
                        >
                          <Tag className="mr-1 inline h-3 w-3" />
                          {normalizeIssueTagLabel(tag)}
                        </button>
                      ))}
                      {savingTagThreadId === selectedThreadId ? <span className="text-[11px] text-slate-300">Menyimpan tag...</span> : null}
                    </div>
                    <div className="mt-3">
                      <Button
                        type="button"
                        size="sm"
                        className="bg-cyan-600 hover:bg-cyan-700 text-white"
                        onClick={() => handleCreateTicket({ includeAiSummary: true })}
                        disabled={creatingTicket || !linkedCustomerId}
                      >
                        {creatingTicket ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Ticket className="mr-2 h-3.5 w-3.5" />}
                        Buka tiket + ringkasan AI
                      </Button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {aiInsight.suggestions.map((text) => (
                        <button key={text} type="button" onClick={() => setComposeText(text)} className="rounded-full border border-cyan-300/30 bg-[#071425] px-3 py-1 text-xs text-cyan-100 hover:bg-cyan-500/20">
                          {text}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {QUICK_REPLIES.map((text) => (
                      <button key={text} type="button" onClick={() => setComposeText(text)} className="rounded-full border border-cyan-400/40 bg-cyan-500/15 px-3 py-1 text-xs text-cyan-200 hover:bg-cyan-500/20">
                        {text}
                      </button>
                    ))}
                  </div>

                  <div className="mt-3 flex gap-2">
                    <input
                      type="text"
                      value={composeText}
                      onChange={(e) => setComposeText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSend();
                      }}
                      placeholder="Tulis balasan ke pelanggan..."
                      className="flex-1 rounded-lg border border-white/20 bg-[#0A0A0A] px-3 py-2 text-sm text-white"
                    />
                    <Button onClick={handleSend} disabled={sending || !composeText.trim()} className="bg-cyan-600 text-white hover:bg-cyan-700">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <SendHorizontal className="h-4 w-4" />}
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex h-[680px] items-center justify-center text-sm text-slate-400">Pilih percakapan dari panel kiri.</div>
              )}
            </div>

            <div className="ktc-list-card rounded-2xl border p-4 shadow-sm">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                <UserRound className="h-4 w-4 text-cyan-300" />
                Konteks Pelanggan
              </h3>

              {contextLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-300"><Loader2 className="h-4 w-4 animate-spin" /> Memuat konteks pelanggan...</div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="rounded-lg border border-white/20 bg-[#0f1b30]/70 p-3">
                    <p className="text-xs text-slate-400">Pelanggan</p>
                    <p className="font-semibold text-white">{customerContext?.customer?.name || customerContext?.customer?.full_name || '-'}</p>
                    <p className="text-xs text-slate-400">{customerContext?.customer?.customer_id || customerContext?.customer?.id || '-'}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-white/20 bg-[#0f1b30]/70 p-2"><p className="text-[11px] text-slate-400">PPPoE</p><p className="text-xs text-white">{customerContext?.context?.service_status || customerContext?.customer?.service_status || '-'}</p></div>
                    <div className="rounded-lg border border-white/20 bg-[#0f1b30]/70 p-2"><p className="text-[11px] text-slate-400">Sinyal (RX)</p><p className="text-xs text-white">{customerContext?.context?.signal_level || customerContext?.customer?.optical_rx_dbm || '-'}</p></div>
                    <div className="rounded-lg border border-white/20 bg-[#0f1b30]/70 p-2"><p className="text-[11px] text-slate-400">Tagihan</p><p className="text-xs text-white">{customerContext?.customer?.billing_state || '-'}</p></div>
                    <div className="rounded-lg border border-white/20 bg-[#0f1b30]/70 p-2"><p className="text-[11px] text-slate-400">Churn Risk</p><p className="text-xs text-white">{customerContext?.context?.churn_risk || (customerContext?.customer?.billing_state === 'overdue' ? 'medium' : 'low')}</p></div>
                  </div>

                  <div className="rounded-lg border border-white/20 bg-[#0f1b30]/70 p-3">
                    <p className="text-xs text-slate-400">Tiket Aktif</p>
                    <p className="text-lg font-semibold text-white">{Number(customerContext?.context?.tickets?.open_count || 0)}</p>
                  </div>

                  <div className={`rounded-lg border p-3 ${slaIndicator.risk === 'high' ? 'border-red-400/40 bg-red-500/10' : slaIndicator.risk === 'medium' ? 'border-amber-400/40 bg-amber-500/10' : 'border-emerald-400/40 bg-emerald-500/10'}`}>
                    <p className="flex items-center gap-2 text-xs font-semibold text-white">
                      {slaIndicator.risk === 'high' ? <AlertTriangle className="h-4 w-4 text-red-300" /> : <CheckCircle2 className="h-4 w-4 text-emerald-300" />}
                      Indikator SLA
                    </p>
                    <p className="mt-1 text-xs text-slate-200">{slaIndicator.label} {slaIndicator.responseMinutes == null ? '' : `(${slaIndicator.responseMinutes} menit)`}</p>
                  </div>

                  <div className="space-y-2 rounded-lg border border-white/20 bg-[#0f1b30]/70 p-3">
                    <p className="text-xs font-semibold text-slate-200">Aksi Cepat</p>
                    <div className="grid grid-cols-2 gap-2">
                      <Button type="button" size="sm" onClick={handleCreateTicket} disabled={creatingTicket || !linkedCustomerId} className="bg-cyan-600 hover:bg-cyan-700 text-white">
                        {creatingTicket ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Ticket className="mr-2 h-3.5 w-3.5" />}Buat Tiket
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="btn-secondary" onClick={() => navigate('/dadu/penanganan', { state: { focusCustomerId: linkedCustomerId } })} disabled={!linkedCustomerId}>
                        <Wrench className="mr-2 h-3.5 w-3.5" />Assign Teknisi
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="btn-secondary" onClick={() => navigate(`/topology?mode=live&sourceType=customer&sourceId=${linkedCustomerId}`)} disabled={!linkedCustomerId}>
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />Cek Perangkat
                      </Button>
                      <Button type="button" size="sm" variant="outline" className="btn-secondary" onClick={() => navigate('/dadu/penagihan', { state: { focusCustomerId: linkedCustomerId } })} disabled={!linkedCustomerId}>
                        <ExternalLink className="mr-2 h-3.5 w-3.5" />Lihat Tagihan
                      </Button>
                    </div>
                  </div>

                <div className="space-y-2 rounded-lg border border-cyan-400/35 bg-cyan-500/10 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-xs font-semibold text-cyan-100">Audit Keputusan AI</p>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="btn-secondary"
                          onClick={() => handleExportAiAuditJson({ includeMessages: false })}
                          disabled={auditExporting || !selectedThread}
                        >
                          {auditExporting ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-2 h-3.5 w-3.5" />}
                          Export Ringkas
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="btn-secondary"
                          onClick={() => handleExportAiAuditJson({ includeMessages: true })}
                          disabled={auditExporting || !selectedThread}
                        >
                          {auditExporting ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Download className="mr-2 h-3.5 w-3.5" />}
                          Export Lengkap
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="bg-cyan-600 hover:bg-cyan-700 text-white"
                          onClick={handleRunAiAudit}
                          disabled={auditLoading || !selectedThread}
                        >
                          {auditLoading ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Bot className="mr-2 h-3.5 w-3.5" />}
                          Simulasikan
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 rounded-md border border-white/15 bg-[#0a1324] p-2">
                      <label className="text-[11px] text-slate-300">
                        Batas Aksi
                        <input
                          type="number"
                          min={1}
                          max={500}
                          value={auditExportLimits.actionLimit}
                          onChange={(e) => setAuditExportLimits((prev) => ({ ...prev, actionLimit: e.target.value }))}
                          onInput={() => setAuditExportPresetKey('custom')}
                          className="mt-1 w-full rounded border border-white/20 bg-[#111827] px-2 py-1 text-xs text-white"
                        />
                      </label>
                      <label className="text-[11px] text-slate-300">
                        Batas Pesan
                        <input
                          type="number"
                          min={1}
                          max={500}
                          value={auditExportLimits.messageLimit}
                          onChange={(e) => setAuditExportLimits((prev) => ({ ...prev, messageLimit: e.target.value }))}
                          onInput={() => setAuditExportPresetKey('custom')}
                          className="mt-1 w-full rounded border border-white/20 bg-[#111827] px-2 py-1 text-xs text-white"
                        />
                      </label>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {auditExportPresets.map((preset) => (
                        <button
                          key={preset.key}
                          type="button"
                          onClick={() => {
                            setAuditExportLimits({ actionLimit: preset.actionLimit, messageLimit: preset.messageLimit });
                            setAuditExportPresetKey(preset.key);
                          }}
                          className={`rounded-full border px-3 py-1 text-[11px] hover:bg-cyan-500/20 ${
                            auditExportPresetKey === preset.key
                              ? 'border-cyan-300/60 bg-cyan-500/25 text-cyan-100'
                              : 'border-cyan-400/40 bg-cyan-500/10 text-cyan-100'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-cyan-100/80">
                      Simulasi tidak menyimpan chat. Dipakai untuk cek keputusan, penanda risiko, dan jejak kebijakan.
                    </p>
                    {auditError ? (
                      <div className="rounded-md border border-rose-400/40 bg-rose-500/10 p-2 text-[11px] text-rose-200">
                        {auditError}
                      </div>
                    ) : null}
                    {auditResult ? (
                      <div className="space-y-2 rounded-md border border-white/15 bg-[#0a1324] p-2 text-[11px]">
                        <div className="grid grid-cols-2 gap-2">
                          <div className="rounded border border-white/10 bg-[#101a2f] p-2">
                            <p className="text-slate-400">Intensi</p>
                            <p className="font-semibold text-white">{auditResult?.intent?.intent_key || '-'}</p>
                          </div>
                          <div className="rounded border border-white/10 bg-[#101a2f] p-2">
                            <p className="text-slate-400">Keputusan</p>
                            <p className="font-semibold text-white">{auditResult?.decision?.decision_type || '-'}</p>
                          </div>
                        </div>
                        <div className="rounded border border-white/10 bg-[#101a2f] p-2">
                          <p className="text-slate-400">Penanda Risiko</p>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(auditResult?.risk_flags || []).length > 0 ? (auditResult.risk_flags.map((flag) => (
                              <span key={flag} className="rounded-full border border-amber-400/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-200">{flag}</span>
                            ))) : <span className="text-slate-300">Tidak ada penanda risiko</span>}
                          </div>
                        </div>
                        <div className="rounded border border-white/10 bg-[#101a2f] p-2">
                          <p className="text-slate-400">Jejak</p>
                          <div className="mt-1 space-y-1">
                            {(auditResult?.explain_trace || []).map((step, idx) => (
                              <div key={`${step?.step || 'step'}-${idx}`} className="rounded border border-white/10 bg-[#0b1528] px-2 py-1">
                                <p className="text-cyan-200">{step?.step || '-'}</p>
                                <p className="text-slate-300">{step?.reason || step?.status || '-'}</p>
                              </div>
                            ))}
                            {(auditResult?.explain_trace || []).length === 0 ? <p className="text-slate-300">Belum ada jejak.</p> : null}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-md border border-white/15 bg-[#0a1324] p-2 text-[11px] text-slate-300">
                        Klik <span className="font-semibold text-cyan-100">Simulasikan</span> untuk melihat audit kebijakan AI.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
                  </PageContainer>
    </AppShell>
    </>
  );
};

export default AdminWhatsAppLive;










